import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UZ_DIG_COMP_AREAS,
  SAMPLE_CAT_QUESTIONS,
  SAMPLE_SCENARIOS,
  CompetenceArea,
  ScenarioTask
} from '@/data/competenceFramework';
import { evaluateFreeTextAnswer, EvaluationResult } from '@/lib/semanticEvaluationService';
import { 
  Building2, 
  ArrowLeft, 
  BarChart3, 
  Users, 
  FileText,
  DollarSign,
  BrainCircuit,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

type ActiveTab = 'overview' | 'cat_test' | 'nlp_assessment' | 'economic_model' | 'candidates';

export default function EmployerAssessmentHub() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [selectedArea, setSelectedArea] = useState<CompetenceArea>(UZ_DIG_COMP_AREAS[5]);

  const [catStep, setCatStep] = useState(0);
  const [catTheta, setCatTheta] = useState(0.0);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [catCompleted, setCatCompleted] = useState(false);

  const [selectedScenario, setSelectedScenario] = useState<ScenarioTask>(SAMPLE_SCENARIOS[0]);
  const [scenarioAnswer, setScenarioAnswer] = useState('');
  const [evalResult, setEvalResult] = useState<EvaluationResult | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);

  const [numEmployees, setNumEmployees] = useState(150);
  const [avgSalaryMln, setAvgSalaryMln] = useState(12);

  const handleAnswerOption = (questionId: string, optionId: string) => {
    setSelectedOptions(prev => ({ ...prev, [questionId]: optionId }));
    const question = SAMPLE_CAT_QUESTIONS.find(q => q.id === questionId);
    if (!question) return;

    const chosen = question.options.find(o => o.id === optionId);
    const isCorrect = chosen?.isCorrect ?? false;

    setCatTheta(prev => {
      const stepSize = 0.4 / (1 + catStep * 0.1);
      return isCorrect ? prev + stepSize : prev - stepSize;
    });

    if (catStep < SAMPLE_CAT_QUESTIONS.length - 1) {
      setCatStep(prev => prev + 1);
    } else {
      setCatCompleted(true);
      toast.success('Тестирование завершено.');
    }
  };

  const handleRunSemanticEvaluation = () => {
    if (!scenarioAnswer.trim()) {
      toast.error('Введите текст решения кейса.');
      return;
    }
    setIsEvaluating(true);
    setTimeout(() => {
      const res = evaluateFreeTextAnswer(
        scenarioAnswer,
        selectedScenario.keywords_ru,
        selectedScenario.sampleExpertAnswer_ru,
        'ru'
      );
      setEvalResult(res);
      setIsEvaluating(false);
      toast.success('Семантическая проверка mBERT выполнена.');
    }, 600);
  };

  const estimatedAnnualSavingsMln = Math.round(
    numEmployees * (avgSalaryMln * 12) * 0.08 + (numEmployees * 2.5)
  );

  return (
    <div className="min-h-screen w-full bg-[#07030e] text-white flex flex-col font-sans relative">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#0d051c]/80 backdrop-blur-2xl px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="glass-btn-secondary px-3.5 py-2 rounded-xl text-xs uppercase tracking-wider flex items-center gap-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>На главную</span>
          </button>

          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white text-[#07030e] font-black text-xs flex items-center justify-center shadow-lg shadow-white/10">
              HR
            </div>
            <div>
              <div className="font-bold text-xs uppercase tracking-wider text-white">
                Центр оценки цифровых компетенций · UzDigComp-AI
              </div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">
                Study Platform · Agrobank · O'zbektelekom · UNICON.UZ
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/interview')}
            className="glass-btn-primary px-4 py-2 rounded-xl text-xs uppercase tracking-wider"
          >
            Симулятор интервью
          </button>
        </div>
      </header>

      <div className="border-b border-white/10 bg-white/[0.02] backdrop-blur-md px-6 py-2.5 overflow-x-auto">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          {[
            { id: 'overview', label: '7 Областей UzDigComp-AI', icon: Building2 },
            { id: 'cat_test', label: 'Адаптивный IRT-тест (-48% вопросов)', icon: BrainCircuit },
            { id: 'nlp_assessment', label: 'NLP/mBERT Оценка кейсов', icon: FileText },
            { id: 'economic_model', label: 'Экономическая модель эффективности', icon: DollarSign },
            { id: 'candidates', label: 'Реестр кандидатов (Банки / IT)', icon: Users },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as ActiveTab)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all whitespace-nowrap ${
                  isActive
                    ? 'glass-btn-primary'
                    : 'glass-btn-secondary text-white/70'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <main className="flex-1 max-w-7xl w-full mx-auto p-4 sm:p-8 space-y-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <div className="glass-panel p-6 sm:p-8 rounded-3xl space-y-4">
              <div className="text-[11px] font-bold text-white/60 uppercase tracking-widest">
                Научно-методическая база оценки компетенций
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-white">
                Модель оценки цифровой компетентности персонала «UzDigComp-AI»
              </h1>
              <p className="text-xs sm:text-sm text-white/70 leading-relaxed max-w-4xl">
                Система разработана для комплексного тестирования выпускников, претендентов на вакансии и сотрудников организаций. Прошла практическую апробацию в филиалах АКБ «Agrobank», АК «O'zbektelekom» и научно-техническом центре «UNICON.UZ».
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3">
                <div className="glass-panel-subtle p-4 rounded-2xl">
                  <div className="text-xl font-bold text-white">7 сфер / 42 индикатора</div>
                  <div className="text-[10px] text-white/50 uppercase font-semibold mt-1">Охват классификатора</div>
                </div>
                <div className="glass-panel-subtle p-4 rounded-2xl">
                  <div className="text-xl font-bold text-white">-48% времени</div>
                  <div className="text-[10px] text-white/50 uppercase font-semibold mt-1">Адаптивный алгоритм CAT</div>
                </div>
                <div className="glass-panel-subtle p-4 rounded-2xl">
                  <div className="text-xl font-bold text-white">187 млрд сум/год</div>
                  <div className="text-[10px] text-white/50 uppercase font-semibold mt-1">Потенциал макроэкономии</div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="text-sm font-bold uppercase tracking-wider text-white flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-white" />
                Структура компетенций UzDigComp-AI
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {UZ_DIG_COMP_AREAS.map((area) => {
                  const isSelected = selectedArea.id === area.id;
                  return (
                    <div
                      key={area.id}
                      onClick={() => setSelectedArea(area)}
                      className={`p-5 rounded-2xl cursor-pointer transition-all ${
                        isSelected
                          ? 'glass-panel border-white/60 shadow-xl shadow-purple-950/50 scale-[1.02]'
                          : 'glass-panel-subtle hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-mono font-bold text-white/60">#{area.id}</span>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/10 text-white border border-white/10">
                          Вес: {area.weight}%
                        </span>
                      </div>
                      <div className="font-bold text-xs text-white mb-1.5">{area.title_ru}</div>
                      <div className="text-xs text-white/60 line-clamp-2 leading-relaxed">{area.description_ru}</div>
                      <div className="mt-3 pt-2.5 border-t border-white/10 text-[10px] text-white/50 font-semibold uppercase">
                        {area.indicatorsCount} индикаторов
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="glass-panel p-6 sm:p-8 rounded-3xl space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-white/50">
                    Детализация индикаторов компетенции #{selectedArea.id}
                  </div>
                  <h3 className="text-lg font-bold text-white mt-1">
                    {selectedArea.title_ru}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveTab('cat_test')}
                  className="glass-btn-primary px-4 py-2 rounded-xl text-xs uppercase tracking-wider"
                >
                  Перейти к тестированию
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedArea.indicators.map((ind) => (
                  <div key={ind.id} className="glass-panel-subtle p-4 rounded-2xl space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-white">{ind.name_ru}</span>
                      <span className="text-[10px] font-mono text-white/40">{ind.code}</span>
                    </div>
                    <div className="text-xs text-white/65 leading-relaxed">{ind.description_ru}</div>
                    <div className="text-[10px] text-white/35 italic pt-1">{ind.name_uz}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'cat_test' && (
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="glass-panel p-6 rounded-3xl space-y-4">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold uppercase tracking-wider text-white">
                  Адаптивное тестирование по модели IRT
                </span>
                <span className="font-mono text-white/50">
                  Вопрос {catStep + 1} / {SAMPLE_CAT_QUESTIONS.length}
                </span>
              </div>

              <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div 
                  className="h-full bg-white transition-all duration-300"
                  style={{ width: `${((catStep + 1) / SAMPLE_CAT_QUESTIONS.length) * 100}%` }}
                />
              </div>

              <div className="flex items-center justify-between text-xs glass-panel-subtle p-3 rounded-xl">
                <div>
                  <span className="text-white/50">Оценка способности (θ):</span>{' '}
                  <span className="font-mono font-bold text-white">{catTheta.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-white/50">Параметр трудности (b):</span>{' '}
                  <span className="font-mono font-bold text-white">
                    {SAMPLE_CAT_QUESTIONS[catStep]?.difficulty.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {!catCompleted ? (
              <div className="glass-panel p-6 sm:p-8 rounded-3xl space-y-5">
                <div className="text-sm sm:text-base font-semibold text-white leading-relaxed">
                  {SAMPLE_CAT_QUESTIONS[catStep]?.question_ru}
                </div>

                <div className="space-y-3">
                  {SAMPLE_CAT_QUESTIONS[catStep]?.options.map((option) => {
                    const isSelected = selectedOptions[SAMPLE_CAT_QUESTIONS[catStep].id] === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleAnswerOption(SAMPLE_CAT_QUESTIONS[catStep].id, option.id)}
                        className={`w-full p-4 rounded-2xl text-left transition-all text-xs sm:text-sm leading-relaxed ${
                          isSelected
                            ? 'glass-btn-primary'
                            : 'glass-btn-secondary text-white/85'
                        }`}
                      >
                        <span className="font-mono font-bold mr-2">{option.id.toUpperCase()})</span>
                        <span>{option.text_ru}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="glass-panel p-8 rounded-3xl text-center space-y-6">
                <div className="w-12 h-12 rounded-full bg-white text-[#07030e] flex items-center justify-center font-bold text-base mx-auto">
                  ✓
                </div>
                <h3 className="text-xl font-bold text-white">Сессия тестирования завершена</h3>
                <p className="text-xs text-white/70 max-w-sm mx-auto">
                  Благодаря адаптивному алгоритму IRT точный профиль квалификации получен за минимальное число шагов.
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                  <div className="glass-panel-subtle p-3.5 rounded-xl">
                    <div className="text-[10px] text-white/50 uppercase">Индекс θ</div>
                    <div className="text-lg font-bold text-white">{catTheta.toFixed(2)}</div>
                  </div>
                  <div className="glass-panel-subtle p-3.5 rounded-xl">
                    <div className="text-[10px] text-white/50 uppercase">Балл</div>
                    <div className="text-lg font-bold text-white">{Math.min(100, Math.round(50 + catTheta * 25))} / 100</div>
                  </div>
                  <div className="glass-panel-subtle p-3.5 rounded-xl">
                    <div className="text-[10px] text-white/50 uppercase">Экономия времени</div>
                    <div className="text-lg font-bold text-white">48%</div>
                  </div>
                  <div className="glass-panel-subtle p-3.5 rounded-xl">
                    <div className="text-[10px] text-white/50 uppercase">Категория</div>
                    <div className="text-lg font-bold text-white">{catTheta >= 0.6 ? 'Квалифицирован' : 'Базовый'}</div>
                  </div>
                </div>

                <div className="flex gap-3 justify-center pt-2">
                  <button
                    type="button"
                    onClick={() => { setCatStep(0); setCatCompleted(false); setSelectedOptions({}); setCatTheta(0); }}
                    className="glass-btn-secondary px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider"
                  >
                    Повторить
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('nlp_assessment')}
                    className="glass-btn-primary px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider"
                  >
                    Практический кейс
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'nlp_assessment' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="glass-panel p-6 sm:p-8 rounded-3xl space-y-5">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/50">
                  Семантический анализатор mBERT / CLAD
                </div>
                <h2 className="text-xl font-bold text-white mt-1">
                  Автоматическая проверка открытых кейсов и регламентов
                </h2>
                <p className="text-xs text-white/70 mt-1">
                  Анализ свободного текста кандидата по ключевым концептам и эталонным формулировкам без ручной проверки экзаменатором.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SAMPLE_SCENARIOS.map((sc) => (
                  <div
                    key={sc.id}
                    onClick={() => { setSelectedScenario(sc); setEvalResult(null); }}
                    className={`p-4 rounded-2xl cursor-pointer transition-all ${
                      selectedScenario.id === sc.id
                        ? 'glass-panel border-white/60 shadow-lg'
                        : 'glass-panel-subtle hover:border-white/30'
                    }`}
                  >
                    <div className="font-bold text-xs text-white">{sc.title_ru}</div>
                    <div className="text-[11px] text-white/60 mt-1 line-clamp-2 leading-relaxed">{sc.scenario_ru}</div>
                  </div>
                ))}
              </div>

              <div className="glass-panel-subtle p-4 rounded-2xl space-y-1.5">
                <div className="text-[11px] font-bold text-white uppercase tracking-wider">Ситуационная задача:</div>
                <div className="text-xs text-white/80 leading-relaxed">{selectedScenario.scenario_ru}</div>
              </div>

              <div className="space-y-3">
                <label className="text-xs font-bold uppercase tracking-wider text-white">
                  Ответ кандидата / алгоритм решения:
                </label>
                <textarea
                  rows={5}
                  value={scenarioAnswer}
                  onChange={(e) => setScenarioAnswer(e.target.value)}
                  placeholder="Опишите регламентные шаги, протоколы защиты данных, используемые сервисы..."
                  className="w-full bg-white/[0.04] border border-white/15 rounded-2xl p-4 text-xs text-white placeholder-white/30 focus:outline-none focus:border-white/50 resize-none"
                />

                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    onClick={() => setScenarioAnswer(selectedScenario.sampleExpertAnswer_ru)}
                    className="text-xs text-white/70 hover:text-white underline"
                  >
                    Заполнить образцовым ответом эксперта
                  </button>

                  <button
                    type="button"
                    onClick={handleRunSemanticEvaluation}
                    disabled={isEvaluating}
                    className="glass-btn-primary px-5 py-2.5 rounded-xl text-xs uppercase tracking-wider"
                  >
                    {isEvaluating ? 'Оценка...' : 'Проверить ответ'}
                  </button>
                </div>
              </div>

              {evalResult && (
                <div className="glass-panel-subtle p-5 rounded-2xl space-y-3 pt-3">
                  <div className="flex items-center justify-between border-b border-white/10 pb-3">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl font-bold text-white">{evalResult.score} / 10</div>
                      <div className="text-xs font-semibold text-white/90">
                        {evalResult.verdict}
                      </div>
                    </div>
                    <div className="text-xs text-white/60 font-mono">
                      Семантическое соответствие: {(evalResult.semanticSimilarity * 100).toFixed(1)}%
                    </div>
                  </div>

                  <div className="text-xs text-white/85 leading-relaxed">
                    {evalResult.feedback_ru}
                  </div>

                  {evalResult.matchedKeywords.length > 0 && (
                    <div className="space-y-1.5">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-white/50">
                        Учтенные терминологические маркеры (CLAD):
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {evalResult.matchedKeywords.map((kw, i) => (
                          <span key={i} className="px-2.5 py-1 rounded-full bg-white/10 text-[11px] text-white border border-white/15">
                            {kw}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'economic_model' && (
          <div className="space-y-6">
            <div className="glass-panel p-6 sm:p-8 rounded-3xl space-y-6">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/50">
                  Экономико-математическая модель эффективности
                </div>
                <h2 className="text-2xl font-bold text-white mt-1">
                  Формула интегрального индекса: E = α·Tₑ + β·A + γ·U
                </h2>
                <p className="text-xs text-white/70 mt-1 max-w-3xl leading-relaxed">
                  Модель сопоставляет сокращение времени аттестации (Tₑ), точность алгоритмической классификации (A) и удовлетворенность пользователей (U) со снижением операционных издержек предприятия.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div className="glass-panel-subtle p-4 rounded-2xl">
                  <div className="text-[10px] text-white/50 uppercase font-semibold">Сокращение времени (Tₑ)</div>
                  <div className="text-2xl font-bold text-white mt-1">55%</div>
                  <div className="text-[11px] text-white/60">с 85 до 38 мин</div>
                  <div className="text-[10px] text-white/40 font-mono mt-2">Вес α = 0.20</div>
                </div>

                <div className="glass-panel-subtle p-4 rounded-2xl">
                  <div className="text-[10px] text-white/50 uppercase font-semibold">Точность NLP (A)</div>
                  <div className="text-2xl font-bold text-white mt-1">F1 = 0.871</div>
                  <div className="text-[11px] text-white/60">QWK = 0.892</div>
                  <div className="text-[10px] text-white/40 font-mono mt-2">Вес β = 0.50</div>
                </div>

                <div className="glass-panel-subtle p-4 rounded-2xl">
                  <div className="text-[10px] text-white/50 uppercase font-semibold">Удовлетворенность (U)</div>
                  <div className="text-2xl font-bold text-white mt-1">4.3 / 5.0</div>
                  <div className="text-[11px] text-white/60">Норма 0.86</div>
                  <div className="text-[10px] text-white/40 font-mono mt-2">Вес γ = 0.30</div>
                </div>

                <div className="glass-panel border-white/40 p-4 rounded-2xl">
                  <div className="text-[10px] text-white uppercase font-bold">Интегральный индекс E</div>
                  <div className="text-3xl font-extrabold text-white mt-1">0.804</div>
                  <div className="text-[11px] text-white/70">Высокая эффективность</div>
                  <div className="text-[10px] text-white/50 font-mono mt-2">Традиционный: ~0.35</div>
                </div>
              </div>

              <div className="pt-4 border-t border-white/10 space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-white">
                  Инвестиционные показатели (данные диссертации)
                </h3>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="glass-panel-subtle p-4 rounded-2xl">
                    <div className="text-[10px] text-white/50 uppercase">NPV (5 лет, 18%)</div>
                    <div className="text-lg font-bold text-white">3 680 млн сум</div>
                  </div>
                  <div className="glass-panel-subtle p-4 rounded-2xl">
                    <div className="text-[10px] text-white/50 uppercase">Индекс PI</div>
                    <div className="text-lg font-bold text-white">5.13</div>
                  </div>
                  <div className="glass-panel-subtle p-4 rounded-2xl">
                    <div className="text-[10px] text-white/50 uppercase">Срок окупаемости</div>
                    <div className="text-lg font-bold text-white">7.3 месяца</div>
                  </div>
                  <div className="glass-panel-subtle p-4 rounded-2xl">
                    <div className="text-[10px] text-white/50 uppercase">Годовая выгода</div>
                    <div className="text-lg font-bold text-white">1 560 млн сум</div>
                  </div>
                </div>
              </div>

              <div className="glass-panel-subtle p-6 rounded-3xl space-y-4">
                <div className="text-xs font-bold uppercase tracking-wider text-white">
                  Расчет плановой экономии для организации:
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-xs">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-white/60">Численность персонала:</span>
                      <span className="font-bold text-white">{numEmployees} чел.</span>
                    </div>
                    <input
                      type="range"
                      min={20}
                      max={1000}
                      step={10}
                      value={numEmployees}
                      onChange={(e) => setNumEmployees(Number(e.target.value))}
                      className="w-full accent-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-white/60">Среднемесячный оклад:</span>
                      <span className="font-bold text-white">{avgSalaryMln} млн сум</span>
                    </div>
                    <input
                      type="range"
                      min={5}
                      max={40}
                      step={1}
                      value={avgSalaryMln}
                      onChange={(e) => setAvgSalaryMln(Number(e.target.value))}
                      className="w-full accent-white"
                    />
                  </div>
                </div>

                <div className="p-4 rounded-2xl glass-panel flex items-center justify-between">
                  <div className="text-xs">
                    <div className="font-semibold text-white">Прогноз ежегодной оптимизации затрат:</div>
                    <div className="text-[11px] text-white/50">Снижение затрат на отбор (-35%), сокращение текучести (-28%) и рост производительности (+11%)</div>
                  </div>
                  <div className="text-2xl font-bold text-white font-mono whitespace-nowrap ml-4">
                    ≈ {estimatedAnnualSavingsMln.toLocaleString()} млн сум
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'candidates' && (
          <div className="space-y-5">
            <div className="glass-panel p-6 rounded-3xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/50">
                  Реестр аттестованных кандидатов и сотрудников
                </div>
                <h2 className="text-xl font-bold text-white mt-0.5">
                  Сводная таблица профилей цифровой компетентности
                </h2>
              </div>

              <button
                type="button"
                onClick={() => toast.success('Формирование сводной выгрузки в формате PDF.')}
                className="glass-btn-secondary px-4 py-2 rounded-xl text-xs uppercase tracking-wider flex items-center gap-2 self-start sm:self-auto"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Экспорт реестра</span>
              </button>
            </div>

            <div className="glass-panel rounded-3xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-white/[0.04] text-white/60 uppercase tracking-wider font-semibold border-b border-white/10">
                    <tr>
                      <th className="p-4">Кандидат / Должность</th>
                      <th className="p-4">Организация</th>
                      <th className="p-4">FinTech & Данные</th>
                      <th className="p-4">AI Literacy</th>
                      <th className="p-4">Итог UzDigComp</th>
                      <th className="p-4">Заключение</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10 text-white">
                    {[
                      { name: 'Абдуллаев Сардор', role: 'Кредитный специалист', org: 'АКБ «Agrobank»', fin: '88%', ai: '74%', total: 84, rec: 'Рекомендован' },
                      { name: 'Каримова Малика', role: 'Frontend инженер', org: 'IT Park / Резидент', fin: '72%', ai: '91%', total: 86, rec: 'Рекомендована' },
                      { name: 'Юсупов Рустам', role: 'Экономист-аналитик', org: 'Аналитический центр', fin: '82%', ai: '60%', total: 76, rec: 'Тренинг по ИИ' },
                      { name: 'Исмаилов Бобур', role: 'Бухгалтер', org: 'Коммерческий банк', fin: '54%', ai: '42%', total: 51, rec: 'Не рекомендован' },
                      { name: 'Рахимова Нигора', role: 'Data Analyst', org: 'ГУП «UNICON.UZ»', fin: '89%', ai: '94%', total: 92, rec: 'Рекомендована' }
                    ].map((row, idx) => (
                      <tr key={idx} className="hover:bg-white/[0.03] transition-colors">
                        <td className="p-4">
                          <div className="font-semibold text-white">{row.name}</div>
                          <div className="text-[11px] text-white/50">{row.role}</div>
                        </td>
                        <td className="p-4 text-white/60">{row.org}</td>
                        <td className="p-4 font-mono font-medium">{row.fin}</td>
                        <td className="p-4 font-mono font-medium">{row.ai}</td>
                        <td className="p-4 font-mono font-bold text-sm">{row.total} / 100</td>
                        <td className="p-4">
                          <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/10 text-white border border-white/20">
                            {row.rec}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
